import { useEffect, useMemo, useState, useCallback } from 'react';
import { Clock, Coffee, WifiOff, Briefcase, Activity, Users, Calendar as CalIcon, Download, ZapOff, Moon } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import api from '../utils/api';
import { toast } from 'sonner';

// ---- helpers --------------------------------------------------------------

const fmtHrs = (h) => {
  if (!h || h <= 0) return '0h';
  const whole = Math.floor(h);
  const min = Math.round((h - whole) * 60);
  if (whole === 0) return `${min}m`;
  if (min === 0) return `${whole}h`;
  return `${whole}h ${min}m`;
};
const fmtMin = (m) => {
  if (!m || m <= 0) return '0m';
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  if (r === 0) return `${h}h`;
  return `${h}h ${r}m`;
};
const toIST = (d) => {
  // Convert a JS Date to YYYY-MM-DD in IST
  const ist = new Date(d.getTime() + (5.5 * 60 - d.getTimezoneOffset()) * 60000);
  return ist.toISOString().slice(0, 10);
};
const today = () => toIST(new Date());
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toIST(d); };
const startOfWeek = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun
  const offset = day === 0 ? 6 : day - 1; // Monday-start week
  d.setDate(d.getDate() - offset);
  return toIST(d);
};
const startOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return toIST(d);
};

const STATUS_PILL = {
  working:      { label: 'Working',       bg: 'bg-emerald-100 text-emerald-700 ring-emerald-200' },
  on_lunch:     { label: 'On lunch',      bg: 'bg-orange-100 text-orange-700 ring-orange-200' },
  network_down: { label: 'Network down',  bg: 'bg-red-100 text-red-700 ring-red-200' },
  away:         { label: 'Away',          bg: 'bg-amber-100 text-amber-700 ring-amber-200' },
  idle:         { label: 'Idle',          bg: 'bg-slate-100 text-slate-500 ring-slate-200' },
};

// ---- main page ------------------------------------------------------------

export default function TeamHours() {
  const [preset, setPreset] = useState('today');
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stopStaleOpen, setStopStaleOpen] = useState(false);
  const [stopStaleHours, setStopStaleHours] = useState(12);
  const [stoppingStale, setStoppingStale] = useState(false);

  const handleStopStale = async () => {
    if (stoppingStale) return;
    setStoppingStale(true);
    try {
      const res = await api.post(`/admin/stop-stale-timers?hours=${stopStaleHours}`);
      const n = res.data?.stopped || 0;
      if (n === 0) {
        toast.success(`No stale timers older than ${stopStaleHours}h were running.`);
      } else {
        const names = (res.data.timers || []).map(t => `${t.user_name} (${t.hours_running}h)`).slice(0, 5).join(', ');
        toast.success(`Stopped ${n} stale timer${n === 1 ? '' : 's'}: ${names}${(res.data.timers || []).length > 5 ? '…' : ''}`);
      }
      setStopStaleOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to stop stale timers');
    } finally {
      setStoppingStale(false);
    }
  };

  const applyPreset = useCallback((p) => {
    setPreset(p);
    if (p === 'today') { setFromDate(today()); setToDate(today()); }
    else if (p === 'yesterday') { const d = daysAgo(1); setFromDate(d); setToDate(d); }
    else if (p === 'this_week') { setFromDate(startOfWeek()); setToDate(today()); }
    else if (p === 'last_7') { setFromDate(daysAgo(6)); setToDate(today()); }
    else if (p === 'this_month') { setFromDate(startOfMonth()); setToDate(today()); }
    else if (p === 'last_30') { setFromDate(daysAgo(29)); setToDate(today()); }
    // 'custom' leaves dates as-is
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [hrsRes, attRes] = await Promise.all([
        api.get('/analytics/team-hours', { params: { from_date: fromDate, to_date: toDate } }),
        api.get('/attendance/team', { params: { from_date: fromDate, to_date: toDate } }).catch(() => ({ data: { users: [] } })),
      ]);
      // Merge attendance summary into each user row (sum across the range)
      const attByUser = {};
      for (const u of attRes.data?.users || []) {
        const total = u.total_minutes || 0;
        let firstIn = null, lastOut = null, anyOpen = false;
        for (const d of u.days || []) {
          if (d.first_punch_in && (!firstIn || d.first_punch_in < firstIn)) firstIn = d.first_punch_in;
          if (d.last_punch_out && (!lastOut || d.last_punch_out > lastOut)) lastOut = d.last_punch_out;
          if (d.is_open) anyOpen = true;
        }
        attByUser[u.user_id] = { total_minutes: total, first_punch_in: firstIn, last_punch_out: lastOut, is_punched_in: anyOpen };
      }
      const merged = (hrsRes.data?.users || []).map(u => {
        const att = attByUser[u.id] || null;
        // Total (Work) = Worked − Lunch − Network Down, expressed in hours.
        // The backend's `total_hours` (task + client time) was confusing because
        // it stayed at 0 unless someone ran a task timer; intuitively the user
        // wants productive on-desk time net of breaks.
        const workedMin = att?.total_minutes || 0;
        const lunchMin = u.lunch_minutes || 0;
        const netDownMin = u.network_downtime_minutes || 0;
        const total_hours = Math.max(0, (workedMin - lunchMin - netDownMin) / 60);
        return { ...u, attendance: att, total_hours: Math.round(total_hours * 100) / 100 };
      });
      setData({ ...hrsRes.data, users: merged });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load team hours');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 60s only when range includes today (live status matters)
  useEffect(() => {
    if (!data?.is_today_range) return;
    const id = setInterval(fetchData, 60000);
    return () => clearInterval(id);
  }, [data?.is_today_range, fetchData]);

  const rows = useMemo(() => {
    const all = data?.users || [];
    if (!search.trim()) return all;
    const s = search.toLowerCase();
    return all.filter(u => u.name.toLowerCase().includes(s));
  }, [data, search]);

  // Aggregates for summary tiles
  const agg = useMemo(() => {
    const a = { task: 0, ctl: 0, lunch: 0, net: 0, total: 0, working: 0, on_lunch: 0, network_down: 0, away: 0 };
    for (const u of rows) {
      a.task += u.task_hours; a.ctl += u.client_time_hours;
      a.lunch += u.lunch_minutes; a.net += u.network_downtime_minutes;
      a.total += u.total_hours;
      if (u.live_status === 'working') a.working++;
      else if (u.live_status === 'on_lunch') a.on_lunch++;
      else if (u.live_status === 'network_down') a.network_down++;
      else if (u.live_status === 'away') a.away++;
    }
    return a;
  }, [rows]);

  const exportCsv = () => {
    if (!rows.length) return;
    const head = ['Name', 'Role', 'Task hours', 'Client time hours', 'Lunch minutes', 'Network down minutes', 'Total hours', 'Live status'];
    const lines = [head.join(',')];
    for (const u of rows) {
      lines.push([u.name, u.role, u.task_hours, u.client_time_hours, u.lunch_minutes, u.network_downtime_minutes, u.total_hours, u.live_status].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `team-hours_${fromDate}_to_${toDate}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="team-hours-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" data-testid="team-hours-heading">
            <Users className="h-7 w-7 text-[#5B6FD6]" /> Team Hours
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Per-employee work hours (Punch-in to Punch-out minus Lunch &amp; Network downtime). Task / Client-time columns show what was logged against work items. Live status updates every minute when viewing today.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setStopStaleOpen(true)} data-testid="stop-stale-timers-btn" className="text-amber-700 border-amber-300 hover:bg-amber-50">
            <ZapOff className="h-4 w-4 mr-1.5" /> Stop stale timers
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows.length} data-testid="team-hours-export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 p-4 bg-white">
        <div className="flex flex-wrap items-end gap-3" data-testid="team-hours-filters">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Range</label>
            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger className="bg-white" data-testid="team-hours-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="this_week">This Week (Mon →)</SelectItem>
                <SelectItem value="last_7">Last 7 days</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_30">Last 30 days</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">From</label>
            <Input type="date" value={fromDate} max={toDate}
              onChange={e => { setFromDate(e.target.value); setPreset('custom'); }}
              className="bg-white w-40" data-testid="team-hours-from" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">To</label>
            <Input type="date" value={toDate} min={fromDate} max={today()}
              onChange={e => { setToDate(e.target.value); setPreset('custom'); }}
              className="bg-white w-40" data-testid="team-hours-to" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Search</label>
            <Input placeholder="Search employee…" value={search} onChange={e => setSearch(e.target.value)} className="bg-white" data-testid="team-hours-search" />
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-1.5 ml-auto">
            <CalIcon className="h-3.5 w-3.5" />
            {data ? `${fromDate} → ${toDate}` : '…'}
            {data?.is_today_range && <span className="text-emerald-600 ml-2 inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live</span>}
          </div>
        </div>
      </Card>

      {/* Aggregate tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="team-hours-agg">
        <AggTile icon={<Briefcase className="h-4 w-4" />} label="Task hours" value={fmtHrs(agg.task)} color="bg-blue-50 text-blue-700" />
        <AggTile icon={<Briefcase className="h-4 w-4" />} label="Client time" value={fmtHrs(agg.ctl)} color="bg-indigo-50 text-indigo-700" />
        <AggTile icon={<Coffee className="h-4 w-4" />} label="Lunch (total)" value={fmtMin(agg.lunch)} color="bg-orange-50 text-orange-700" />
        <AggTile icon={<WifiOff className="h-4 w-4" />} label="Network down" value={fmtMin(agg.net)} color="bg-red-50 text-red-700" />
        <AggTile icon={<Clock className="h-4 w-4" />} label="Work total" value={fmtHrs(agg.total)} color="bg-emerald-50 text-emerald-700 font-bold" />
      </div>

      {/* Live status summary chip strip (only when today's in range) */}
      {data?.is_today_range && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="team-hours-live-strip">
          <span className="text-slate-500 font-medium uppercase tracking-wider">Right now:</span>
          <Pill bg={STATUS_PILL.working.bg}>{agg.working} working</Pill>
          <Pill bg={STATUS_PILL.on_lunch.bg}>{agg.on_lunch} on lunch</Pill>
          <Pill bg={STATUS_PILL.network_down.bg}>{agg.network_down} network down</Pill>
          <Pill bg={STATUS_PILL.away.bg}>{agg.away} away</Pill>
          <Pill bg={STATUS_PILL.idle.bg}>{rows.length - agg.working - agg.on_lunch - agg.network_down - agg.away} idle</Pill>
        </div>
      )}

      {/* Table */}
      <Card className="border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-hours-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Employee</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                {data?.is_today_range && <th className="text-left px-4 py-3 font-semibold">Status</th>}
                <th className="text-left px-4 py-3 font-semibold">Punch In</th>
                <th className="text-left px-4 py-3 font-semibold">Punch Out</th>
                <th className="text-right px-4 py-3 font-semibold">Worked</th>
                <th className="text-right px-4 py-3 font-semibold">Task</th>
                <th className="text-right px-4 py-3 font-semibold">Client time</th>
                <th className="text-right px-4 py-3 font-semibold">Lunch</th>
                <th className="text-right px-4 py-3 font-semibold">Network down</th>
                <th className="text-right px-4 py-3 font-semibold">Total (work)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={data?.is_today_range ? 11 : 10} className="text-center text-slate-400 py-10">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={data?.is_today_range ? 11 : 10} className="text-center text-slate-400 py-10">No employees match.</td></tr>
              ) : rows.map(u => {
                const att = u.attendance;
                const fmtTime = (s) => s ? new Date(s).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
                return (
                <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`team-hours-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#5B6FD6] to-[#10B981] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {u.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <span className="font-medium text-slate-800">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="text-xs text-slate-500 capitalize">{u.role}</span></td>
                  {data?.is_today_range && (
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full ring-1 ${STATUS_PILL[u.live_status].bg}`} data-testid={`team-hours-status-${u.id}`}>
                        {u.live_status === 'working' && <Activity className="h-3 w-3" />}
                        {u.live_status === 'on_lunch' && <Coffee className="h-3 w-3" />}
                        {u.live_status === 'network_down' && <WifiOff className="h-3 w-3" />}
                        {u.live_status === 'away' && <Moon className="h-3 w-3" />}
                        {STATUS_PILL[u.live_status].label}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono text-slate-700 text-xs" data-testid={`punch-in-${u.id}`}>{fmtTime(att?.first_punch_in)}</td>
                  <td className="px-4 py-3 font-mono text-slate-700 text-xs">
                    {att?.is_punched_in ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> in
                      </span>
                    ) : fmtTime(att?.last_punch_out)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{att ? fmtMin(att.total_minutes) : '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{fmtHrs(u.task_hours)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{fmtHrs(u.client_time_hours)}</td>
                  <td className="px-4 py-3 text-right font-mono text-orange-600">{fmtMin(u.lunch_minutes)}</td>
                  <td className="px-4 py-3 text-right font-mono text-red-600">{fmtMin(u.network_downtime_minutes)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">{fmtHrs(u.total_hours)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Stop stale timers dialog */}
      <AlertDialog open={stopStaleOpen} onOpenChange={setStopStaleOpen}>
        <AlertDialogContent data-testid="stop-stale-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ZapOff className="h-5 w-5 text-amber-600" /> Stop stale running timers
            </AlertDialogTitle>
            <AlertDialogDescription>
              Stops every task-timer that has been running for longer than the threshold below.
              Stale timers usually mean someone forgot to stop them — their accumulated time is
              discarded (no fake time-log is created) so reports stay clean. The user is notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-3">
            <label className="text-sm font-medium text-slate-700 block mb-1.5">Threshold (hours)</label>
            <Input type="number" min={1} max={168} value={stopStaleHours}
              onChange={e => setStopStaleHours(Number(e.target.value) || 12)}
              className="w-32" data-testid="stop-stale-hours-input" />
            <p className="text-xs text-slate-500 mt-2">
              Recommended: 12 hours. Any timer started before this duration ago will be stopped.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="stop-stale-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStopStale}
              disabled={stoppingStale}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="stop-stale-confirm"
            >
              {stoppingStale ? 'Stopping…' : 'Stop stale timers'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const AggTile = ({ icon, label, value, color }) => (
  <div className={`rounded-lg px-3 py-2.5 ${color}`}>
    <div className="flex items-center gap-1.5 mb-1 opacity-80">
      {icon}
      <span className="text-[11px] uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-lg font-mono font-semibold">{value}</p>
  </div>
);

const Pill = ({ bg, children }) => (
  <span className={`px-2.5 py-1 rounded-full ring-1 font-semibold text-[11px] ${bg}`}>{children}</span>
);
